/**
 * Composable to fetch items where the current user is assigned/tagged (bugs, features, etc.)
 * Used by the top-nav notification dropdown to show "You have been tagged under B00123" with links.
 */

import { ref, computed } from 'vue'
import { useAuthStore } from '@/stores/auth'
import { http } from '@/utils/httpClient'

const LIMIT_PER_TYPE = 10
const STORAGE_KEY = 'hl_assigned_notifications_read'

export type AssignedNotificationItem = {
  type: 'bug' | 'feature' | 'accessibility' | 'test-case'
  id: string
  displayId: string // e.g. B00123, F00001, TC00001
  title?: string
  project_id?: string
  read: boolean
  selfAssigned: boolean // true when the logged-in user assigned it to themselves
  sortAt: number // timestamp for ordering (newest first)
  to: { path: string; query?: Record<string, string> }
}

function toSortTime(d: any): number {
  if (d == null) return 0
  if (typeof d === 'number') return d
  const t = new Date(d).getTime()
  return Number.isNaN(t) ? 0 : t
}

function getReadStorage(userId: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const all = JSON.parse(raw) as Record<string, Record<string, boolean>>
    return all[userId] ?? {}
  } catch {
    return {}
  }
}

function setReadStorage(userId: string, key: string, read: boolean) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const all: Record<string, Record<string, boolean>> = raw ? JSON.parse(raw) : {}
    if (!all[userId]) all[userId] = {}
    if (read) {
      all[userId][key] = true
    } else {
      delete all[userId][key]
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
  } catch (e) {
    console.warn('Failed to persist notification read state', e)
  }
}

function storageKey(type: string, id: string) {
  return `${type}:${id}`
}

export function useAssignedNotifications() {
  const authStore = useAuthStore()
  const loading = ref(false)
  const error = ref<string | null>(null)
  const items = ref<AssignedNotificationItem[]>([])

  const currentUserId = computed(() => authStore.user?.id || authStore.user?.uid)

  function buildBugLink(bug: { id: string; project_id?: string }) {
    const projectId = bug.project_id
    if (projectId) {
      return { path: `/projects/${projectId}/bugs/${bug.id}`, query: { from: 'assigned-to-me' } }
    }
    return { path: `/bugs/${bug.id}`, query: { from: 'assigned-to-me' } }
  }

  function buildFeatureLink(feature: { id: string; project_id?: string }) {
    const projectId = feature.project_id
    if (projectId) {
      return { path: `/projects/${projectId}/features/${feature.id}`, query: { from: 'assigned-to-me' } }
    }
    return { path: '/assigned-to-me', query: { tab: 'features' } }
  }

  function buildAccessibilityLink(issue: { id?: string; issue_id?: string }) {
    const id = issue.id || issue.issue_id
    return {
      path: '/reports',
      query: { tab: 'accessibility', view: 'detailed', from: 'assigned-to-me', issueId: id }
    }
  }

  function buildTestCaseLink(tc: { id: string; project_id?: string }) {
    const projectId = tc.project_id
    if (projectId) {
      return { path: `/projects/${projectId}/test-cases/${tc.id}`, query: { from: 'assigned-to-me' } }
    }
    return { path: '/assigned-to-me', query: { tab: 'test-runs' } }
  }

  async function fetchAssignedItems() {
    const userId = currentUserId.value
    if (!userId) {
      items.value = []
      return
    }

    loading.value = true
    error.value = null
    const results: AssignedNotificationItem[] = []

    try {
      // Bugs: assigned_to = userId
      const bugParams = new URLSearchParams({
        assigned_to: userId,
        limit: String(LIMIT_PER_TYPE),
        page: '1'
      })
      const bugRes = await http.get(`/bugs?${bugParams}`)
      const bugList = Array.isArray(bugRes) ? bugRes : (bugRes?.data ?? [])
      const bugs = (bugList || []).filter((b: any) => !b.is_deleted && !b.isDeleted)
      const readState = getReadStorage(userId)
      bugs.forEach((bug: any) => {
        const key = storageKey('bug', bug.id)
        const creatorId = bug.created_by || bug.createdBy
        results.push({
          type: 'bug',
          id: bug.id,
          displayId: bug.bugId || bug.id,
          title: bug.title,
          project_id: bug.project_id,
          read: !!readState[key],
          selfAssigned: creatorId === userId,
          sortAt: toSortTime(bug.updated_at ?? bug.created_at),
          to: buildBugLink(bug)
        })
      })

      // Features: dev_assignee or qa_assignee = userId (two requests, then merge by id)
      const featureParams = new URLSearchParams({
        limit: String(LIMIT_PER_TYPE),
        page: '1'
      })
      const [devRes, qaRes] = await Promise.all([
        http.get(`/features?dev_assignee=${encodeURIComponent(userId)}&${featureParams}`),
        http.get(`/features?qa_assignee=${encodeURIComponent(userId)}&${featureParams}`)
      ])
      const devList = Array.isArray(devRes) ? devRes : (devRes?.data ?? [])
      const qaList = Array.isArray(qaRes) ? qaRes : (qaRes?.data ?? [])
      const seenIds = new Set<string>()
      ;[...devList, ...qaList].forEach((feature: any) => {
        if (feature.is_deleted || feature.isDeleted) return
        if (seenIds.has(feature.id)) return
        seenIds.add(feature.id)
        const key = storageKey('feature', feature.id)
        const featureCreator = feature.created_by || feature.createdBy
        results.push({
          type: 'feature',
          id: feature.id,
          displayId: feature.featureId || feature.id,
          title: feature.title,
          project_id: feature.project_id,
          read: !!readState[key],
          selfAssigned: featureCreator === userId,
          sortAt: toSortTime(feature.updated_at ?? feature.created_at),
          to: buildFeatureLink(feature)
        })
      })

      // Accessibility: assigned_to
      try {
        const accParams = new URLSearchParams()
        accParams.append('assigned_to', userId)
        accParams.append('limit', String(LIMIT_PER_TYPE))
        const accRes = await http.get(`/accessibility/issues?${accParams}`)
        const accData = accRes?.data ?? accRes
        const accList = Array.isArray(accData) ? accData : (accData?.items ?? [])
        ;(accList || []).forEach((issue: any) => {
          if (issue.is_deleted) return
          const id = issue.id || issue.issue_id
          const key = storageKey('accessibility', id || issue._id)
          const accAssigner = issue.assigned_by
          results.push({
            type: 'accessibility',
            id: id || issue._id,
            displayId: issue.issue_id || id || 'A11Y',
            title: issue.description?.slice(0, 50) || issue.page_url,
            read: !!readState[key],
            selfAssigned: accAssigner === userId,
            sortAt: toSortTime(issue.updated_at ?? issue.created_at),
            to: buildAccessibilityLink(issue)
          })
        })
      } catch {
        // Accessibility endpoint may not exist or differ; skip
      }

      // Test cases: assigned_to = userId
      try {
        const tcParams = new URLSearchParams({
          assigned_to: userId,
          limit: String(LIMIT_PER_TYPE),
          page: '1'
        })
        const tcRes = await http.get(`/test_cases?${tcParams}`)
        const tcList = Array.isArray(tcRes) ? tcRes : (tcRes?.data ?? [])
        const testCases = (tcList || []).filter((tc: any) => !tc.is_deleted && !tc.isDeleted)
        testCases.forEach((tc: any) => {
          const key = storageKey('test-case', tc.id)
          const tcCreator = tc.created_by || tc.createdBy
          results.push({
            type: 'test-case',
            id: tc.id,
            displayId: tc.testCaseId || tc.id,
            title: tc.title || tc.name || undefined,
            project_id: tc.project_id,
            read: !!readState[key],
            selfAssigned: tcCreator === userId,
            sortAt: toSortTime(tc.updated_at ?? tc.created_at),
            to: buildTestCaseLink(tc)
          })
        })
      } catch (e) {
        console.warn('Failed to fetch assigned test cases for notifications', e)
      }

      // Sort by when the item was last updated/created (newest first = order it got fired)
      results.sort((a, b) => (b.sortAt || 0) - (a.sortAt || 0))
      items.value = results
    } catch (e: any) {
      console.error('Failed to fetch assigned notifications:', e)
      error.value = e?.message || 'Failed to load'
      items.value = []
    } finally {
      loading.value = false
    }
  }

  function removeItem(item: AssignedNotificationItem) {
    items.value = items.value.filter(
      (i) => !(i.type === item.type && i.id === item.id)
    )
  }

  function markAsRead(item: AssignedNotificationItem) {
    const found = items.value.find((i) => i.type === item.type && i.id === item.id)
    if (found) {
      found.read = true
      const uid = currentUserId.value
      if (uid) setReadStorage(uid, storageKey(item.type, item.id), true)
    }
  }

  function markAllAsRead() {
    const uid = currentUserId.value
    items.value.forEach((item) => {
      item.read = true
      if (uid) setReadStorage(uid, storageKey(item.type, item.id), true)
    })
  }

  const unreadCount = computed(() => items.value.filter((i) => !i.read).length)

  return {
    items,
    loading,
    error,
    currentUserId,
    unreadCount,
    fetchAssignedItems,
    removeItem,
    markAsRead,
    markAllAsRead
  }
}
